/**
 * Маршруты приложения.
 *
 * Роутинг описан кодом, а не файловой генерацией: дерево маршрутов маленькое,
 * а сгенерированный routeTree.gen.ts — ещё один артефакт, который надо держать
 * в актуальном состоянии и ревьюить.
 *
 * Пульт грузится сразу, админка и отчёты — ленивыми чанками: диспетчер за смену
 * не открывает ни одного из этих экранов, и тянуть их в его бандл незачем (§2).
 */

import type { ReactNode } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';

import { AppShell } from './app-shell.js';
import { AuthGuard } from './auth-guard.js';
import { DeskPage } from '../pages/desk.page.js';
import { LoginPage } from '../pages/login.page.js';

const rootRoute = createRootRoute({ component: AppShell });

/** Разделы админки и отчётов: роль проверяется здесь для показа и на сервере — для доступа */
function adminOnly(children: ReactNode): React.JSX.Element {
  return <AuthGuard roles={['admin', 'supervisor']}>{children}</AuthGuard>;
}

/** Журнал действий и упавшие задачи: /api/admin/* открыт только роли admin */
function strictAdminOnly(children: ReactNode): React.JSX.Element {
  return <AuthGuard roles={['admin']}>{children}</AuthGuard>;
}

const AdminOverviewPage = lazyRouteComponent(
  () => import('../pages/admin-overview.page.js'),
  'AdminOverviewPage',
);
const AdminQueuePage = lazyRouteComponent(
  () => import('../pages/admin-queue.page.js'),
  'AdminQueuePage',
);
const AdminDispatchersPage = lazyRouteComponent(
  () => import('../pages/admin-dispatchers.page.js'),
  'AdminDispatchersPage',
);
const AdminSourcesPage = lazyRouteComponent(
  () => import('../pages/admin-sources.page.js'),
  'AdminSourcesPage',
);
const AdminCabinetsPage = lazyRouteComponent(
  () => import('../pages/admin-cabinets.page.js'),
  'AdminCabinetsPage',
);
const AdminUploadPage = lazyRouteComponent(
  () => import('../pages/admin-upload.page.js'),
  'AdminUploadPage',
);
const AdminContactsPage = lazyRouteComponent(
  () => import('../pages/admin-contacts.page.js'),
  'AdminContactsPage',
);
const AdminLabelsPage = lazyRouteComponent(
  () => import('../pages/admin-labels.page.js'),
  'AdminLabelsPage',
);
const AdminRecordingsPage = lazyRouteComponent(
  () => import('../pages/admin-recordings.page.js'),
  'AdminRecordingsPage',
);
const AdminRoundsPage = lazyRouteComponent(
  () => import('../pages/admin-rounds.page.js'),
  'AdminRoundsPage',
);
const AdminSettingsPage = lazyRouteComponent(
  () => import('../pages/admin-settings.page.js'),
  'AdminSettingsPage',
);
const AdminTelephonyPage = lazyRouteComponent(
  () => import('../pages/admin-telephony.page.js'),
  'AdminTelephonyPage',
);
const AdminIntegrationsPage = lazyRouteComponent(
  () => import('../pages/admin-integrations.page.js'),
  'AdminIntegrationsPage',
);
const AdminPressOnePage = lazyRouteComponent(
  () => import('../pages/admin-press-one.page.js'),
  'AdminPressOnePage',
);
const AdminAuditPage = lazyRouteComponent(
  () => import('../pages/admin-audit.page.js'),
  'AdminAuditPage',
);
const AdminPayPage = lazyRouteComponent(() => import('../pages/admin-pay.page.js'), 'AdminPayPage');
const StatsDispatchersPage = lazyRouteComponent(
  () => import('../pages/stats-dispatchers.page.js'),
  'StatsDispatchersPage',
);
const StatsSourcesPage = lazyRouteComponent(
  () => import('../pages/stats-sources.page.js'),
  'StatsSourcesPage',
);
const StatsReachPage = lazyRouteComponent(
  () => import('../pages/stats-reach.page.js'),
  'StatsReachPage',
);

const StatsLeadsPage = lazyRouteComponent(
  () => import('../pages/stats-leads.page.js'),
  'StatsLeadsPage',
);

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // Переход в роутере выполняется броском специального объекта, а не ошибки:
    // это его штатный способ прервать загрузку маршрута
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/desk' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const deskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/desk',
  // ?dispatcher=<id> — админ и супервизор смотрят пульт глазами диспетчера.
  // Число и строка равноправны (сериализатор роутера пишет числа без кавычек),
  // мусор молча выбрасывается: пульт открывается свой
  validateSearch: (search: Record<string, unknown>): { dispatcher?: string } => {
    const raw = search['dispatcher'];
    const value = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw : '';
    return /^\d+$/.test(value) ? { dispatcher: value } : {};
  },
  component: () => (
    <AuthGuard>
      <DeskPage />
    </AuthGuard>
  ),
});

const adminOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/overview',
  component: () => adminOnly(<AdminOverviewPage />),
});

const adminQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/queue',
  component: () => adminOnly(<AdminQueuePage />),
});

const adminDispatchersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/dispatchers',
  component: () => adminOnly(<AdminDispatchersPage />),
});

const adminSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/sources',
  component: () => adminOnly(<AdminSourcesPage />),
});

const adminCabinetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/cabinets',
  component: () => adminOnly(<AdminCabinetsPage />),
});

/** Загрузка номеров: маршрут импорта на сервере открыт только роли admin */
const adminUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/upload',
  component: () => strictAdminOnly(<AdminUploadPage />),
});

const adminContactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/contacts',
  component: () => adminOnly(<AdminContactsPage />),
});

const adminLabelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/labels',
  component: () => adminOnly(<AdminLabelsPage />),
});

const adminRecordingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/recordings',
  component: () => adminOnly(<AdminRecordingsPage />),
});

const adminRoundsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/rounds',
  component: () => adminOnly(<AdminRoundsPage />),
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings',
  component: () => adminOnly(<AdminSettingsPage />),
});

/**
 * Телефония открыта и супервайзеру: справочники он читает (GET разрешён обеим
 * ролям), а на правку транка, номера и правила сервер ответит ему 403 —
 * решение о доступе принимает маршрут, а не видимость пункта меню.
 */
const adminTelephonyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/telephony',
  component: () => adminOnly(<AdminTelephonyPage />),
});

const adminPressOneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/press-one',
  component: () => adminOnly(<AdminPressOnePage />),
});

const adminIntegrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/integrations',
  component: () => adminOnly(<AdminIntegrationsPage />),
});

const adminAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/audit',
  component: () => strictAdminOnly(<AdminAuditPage />),
});

const adminPayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats/pay',
  component: () => strictAdminOnly(<AdminPayPage />),
});

const statsDispatchersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats/dispatchers',
  component: () => adminOnly(<StatsDispatchersPage />),
});

const statsSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats/sources',
  component: () => adminOnly(<StatsSourcesPage />),
});

const statsReachRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats/reach',
  component: () => adminOnly(<StatsReachPage />),
});

const statsLeadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats/leads',
  component: () => adminOnly(<StatsLeadsPage />),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  deskRoute,
  adminOverviewRoute,
  adminQueueRoute,
  adminDispatchersRoute,
  adminSourcesRoute,
  adminCabinetsRoute,
  adminUploadRoute,
  statsReachRoute,
  adminContactsRoute,
  adminLabelsRoute,
  adminRoundsRoute,
  adminRecordingsRoute,
  adminSettingsRoute,
  adminTelephonyRoute,
  adminPressOneRoute,
  adminIntegrationsRoute,
  adminAuditRoute,
  adminPayRoute,
  statsDispatchersRoute,
  statsSourcesRoute,
  statsLeadsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: () => (
    <main className="p-8">
      <h1 className="text-xl font-medium">Страница не найдена</h1>
    </main>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
